class GoogleDriveService {
  constructor() {
    this.isLoaded = false;
    this.accessToken = null;
    this.tokenExpiresAt = null;
    this.config = {
      CLIENT_ID: process.env.REACT_APP_GOOGLE_DRIVE_CLIENT_ID,
      API_KEY: process.env.REACT_APP_GOOGLE_DRIVE_API_KEY,
      DISCOVERY_DOC: 'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
      SCOPES: 'https://www.googleapis.com/auth/drive.file',
      FOLDER_ID: process.env.REACT_APP_GOOGLE_DRIVE_FOLDER_ID || 'root'
    };

    // Recuperar token salvo na sessão, se existir
    const savedToken = sessionStorage.getItem('gdrive_access_token');
    const savedExpires = sessionStorage.getItem('gdrive_token_expires_at');
    if (savedToken && savedExpires && Number(savedExpires) > Date.now()) {
      this.accessToken = savedToken;
      this.tokenExpiresAt = Number(savedExpires);
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  async loadGoogleAPIs() {
    if (this.isLoaded) return;

    return new Promise((resolve, reject) => {
      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.onload = () => {
        const gapiScript = document.createElement('script');
        gapiScript.src = 'https://apis.google.com/js/api.js';
        gapiScript.onload = async () => {
          try {
            await new Promise(resolve => window.gapi.load('client', resolve));
            await window.gapi.client.init({
              apiKey: this.config.API_KEY,
              discoveryDocs: [this.config.DISCOVERY_DOC]
            });
            
            this.isLoaded = true;
            console.log('✅ Google APIs carregadas com nova versão');
            resolve();
          } catch (error) {
            console.error('❌ Erro ao carregar Google APIs:', error);
            reject(error);
          }
        };
        gapiScript.onerror = reject;
        document.head.appendChild(gapiScript);
      };
      gisScript.onerror = reject;
      document.head.appendChild(gisScript);
    });
  }

  async authenticate(force = false) {
    try {
      await this.loadGoogleAPIs();

      if (!force && this.isAuthenticated()) {
        return true;
      }

      if (!force) {
        const silentAuth = await this.trySilentAuth();
        if (silentAuth) {
          return true;
        }
      }

      console.log('🔐 Iniciando autenticação interativa...');

      if (this.isIOS()) {
        return new Promise((resolve, reject) => {
          window.google.accounts.oauth2.initTokenClient({
            client_id: this.config.CLIENT_ID,
            scope: this.config.SCOPES,
            prompt: 'consent',
            callback: (tokenResponse) => {
              if (tokenResponse.error || !tokenResponse.access_token) {
                alert('No iPhone/iPad, não foi possível autenticar automaticamente no Google Drive. Permita cookies e pop-ups ou tente outro navegador.');
                reject(new Error('Autenticação falhou no iOS.'));
                return;
              }
              this.accessToken = tokenResponse.access_token;
              this.tokenExpiresAt = Date.now() + (tokenResponse.expires_in ? tokenResponse.expires_in * 1000 : 3600 * 1000);
              sessionStorage.setItem('gdrive_access_token', this.accessToken);
              sessionStorage.setItem('gdrive_token_expires_at', String(this.tokenExpiresAt));
              console.log('✅ Usuário autenticado no Google Drive (iOS)');
              resolve(true);
            }
          }).requestAccessToken();
        });
      }

      return new Promise((resolve, reject) => {
        window.google.accounts.oauth2.initTokenClient({
          client_id: this.config.CLIENT_ID,
          scope: this.config.SCOPES,
          callback: (tokenResponse) => {
            if (tokenResponse.error) {
              console.error('❌ Erro na autenticação:', tokenResponse.error);
              reject(new Error('Falha na autenticação com Google Drive'));
              return;
            }
            this.accessToken = tokenResponse.access_token;
            this.tokenExpiresAt = Date.now() + (tokenResponse.expires_in ? tokenResponse.expires_in * 1000 : 3600 * 1000);
            sessionStorage.setItem('gdrive_access_token', this.accessToken);
            sessionStorage.setItem('gdrive_token_expires_at', String(this.tokenExpiresAt));
            console.log('✅ Usuário autenticado no Google Drive');
            resolve(true);
          }
        }).requestAccessToken();
      });

    } catch (error) {
      console.error('❌ Erro na autenticação:', error);
      throw new Error('Falha na autenticação com Google Drive');
    }
  }

  async uploadFile(file, fileName = null) {
    try {
      await this.authenticate();

      const timestamp = Date.now();
      const finalFileName = fileName || `arena_${timestamp}_${file.name}`;

      console.log('📤 Fazendo upload para Google Drive:', finalFileName);

      const metadata = {
        name: finalFileName,
        parents: [this.config.FOLDER_ID]
      };

      const formData = new FormData();
      formData.append('metadata', new Blob([JSON.stringify(metadata)], {type: 'application/json'}));
      formData.append('file', file);

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Upload concluído:', result);

      await this.makeFilePublic(result.id);

      return {
        id: result.id,
        name: result.name,
        publicUrl: `https://drive.google.com/uc?id=${result.id}`,
        downloadUrl: `https://drive.google.com/uc?export=download&id=${result.id}`,
        thumbnailUrl: `https://drive.google.com/thumbnail?id=${result.id}&sz=w400`
      };

    } catch (error) {
      console.error('❌ Erro no upload:', error);
      throw new Error(`Falha no upload: ${error.message}`);
    }
  }

  async makeFilePublic(fileId) {
    try {
      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: 'reader',
          type: 'anyone'
        })
      });

      if (response.ok) {
        console.log('✅ Arquivo tornado público:', fileId);
      } else {
        console.warn('⚠️ Não foi possível tornar o arquivo público');
      }
    } catch (error) {
      console.warn('⚠️ Erro ao tornar arquivo público:', error.message);
    }
  }

  async deleteFile(fileId) {
    try {
      await this.authenticate();

      const response = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      if (response.ok) {
        console.log('✅ Arquivo deletado do Google Drive:', fileId);
        return true;
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

    } catch (error) {
      console.error('❌ Erro ao deletar arquivo:', error);
      throw new Error(`Falha ao deletar: ${error.message}`);
    }
  }

  isConfigured() {
    return !!(this.config.CLIENT_ID && this.config.API_KEY);
  }

  isAuthenticated() {
    return !!(this.accessToken && this.tokenExpiresAt && this.tokenExpiresAt > Date.now());
  }

  canTrySilentAuth() {
    const savedExpires = sessionStorage.getItem('gdrive_token_expires_at');
    if (savedExpires) {
      const expirationTime = Number(savedExpires);
      const now = Date.now();
      const twentyFourHoursAgo = now - (24 * 60 * 60 * 1000);
      return expirationTime > twentyFourHoursAgo;
    }
    return false;
  }

  async trySilentAuth() {
    if (!this.canTrySilentAuth() || this.isAuthenticated()) {
      return this.isAuthenticated();
    }

    try {
      await this.loadGoogleAPIs();
      
      return new Promise((resolve) => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: this.config.CLIENT_ID,
          scope: this.config.SCOPES,
          prompt: '',
          callback: (tokenResponse) => {
            if (tokenResponse.error || !tokenResponse.access_token) {
              console.log('ℹ️ Autenticação silenciosa não disponível');
              resolve(false);
              return;
            }
            this.accessToken = tokenResponse.access_token;
            this.tokenExpiresAt = Date.now() + (tokenResponse.expires_in ? tokenResponse.expires_in * 1000 : 3600 * 1000);
            sessionStorage.setItem('gdrive_access_token', this.accessToken);
            sessionStorage.setItem('gdrive_token_expires_at', String(this.tokenExpiresAt));
            console.log('✅ Autenticação silenciosa bem-sucedida');
            resolve(true);
          }
        });
        
        tokenClient.requestAccessToken({ prompt: '' });
      });
    } catch (error) {
      console.log('ℹ️ Autenticação silenciosa falhou:', error.message);
      return false;
    }
  }

  async signOut() {
    try {
      if (this.accessToken && window.google?.accounts?.oauth2) {
        window.google.accounts.oauth2.revoke(this.accessToken);
      }
      this.accessToken = null;
      console.log('✅ Logout realizado');
    } catch (error) {
      console.error('❌ Erro no logout:', error);
    }
  }
}

export const googleDriveService = new GoogleDriveService();

export const isGoogleDriveConfigured = () => {
  return googleDriveService.isConfigured();
};

export const uploadToStorage = async (file, fileName = null) => {
  if (isGoogleDriveConfigured()) {
    try {
      console.log('📁 Tentando usar Google Drive para upload');
      return await googleDriveService.uploadFile(file, fileName);
    } catch (error) {
      console.error('❌ Falha no Google Drive:', error.message);
      throw new Error(`Google Drive não está disponível. Configure as variáveis de ambiente do Google Drive: ${error.message}`);
    }
  }  
  throw new Error('Google Drive não está configurado. Configure as variáveis de ambiente REACT_APP_GOOGLE_DRIVE_CLIENT_ID e REACT_APP_GOOGLE_DRIVE_API_KEY.');
};
